import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
// SECURITY NOTE: Referral lookup moved to API endpoint for security (needed to scan all users)
// These imports kept for sendCompleteDataToKlaviyo (user accessing their own data only)
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';

import UserInformationForm from './1-UserInfoForm';
import StripePaymentForm from './2-StripeContainerForPaymentForm';
import StudentProfileForm from './3-CreateStudentProfile';
import CreateEducatorProfile from './CreateEducatorProfile';
import { UserData } from '../UserAndProfile/UserContext';
import { trackPixelEvent, PixelEvents } from '../../utils/meta/metaPixel';
import { PaymentFormUserProps } from './PaymentForm';
import { shouldShowPricing } from '../../utils/accessMode';

// Define onboarding stages for clarity and potential use in a stepper
const STAGES = {
  USER_INFO: 'userInfo',
  // PAYMENT: 'payment', // Comment out PAYMENT stage on 5/31/2025 to not require credit card immediately.
  STUDENT_PROFILE: 'studentProfile',
  EDUCATOR_PROFILE: 'educatorProfile',
  COMPLETED: 'completed',
  ERROR: 'error',
} as const;

type OnboardingStage = typeof STAGES[keyof typeof STAGES];

const stepDescriptions: { [key in Exclude<OnboardingStage, 'completed' | 'error'>]?: string } = {
  [STAGES.USER_INFO]: 'Account Details',
  // [STAGES.PAYMENT]: 'Payment Information', // Comment out PAYMENT description on 5/31/2025 to not require credit card immediately.
  [STAGES.STUDENT_PROFILE]: 'Student Profile',
  [STAGES.EDUCATOR_PROFILE]: 'Educator Profile',
};

const StartFreeTrialRatio: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [onboardingStage, setOnboardingStage] = useState<OnboardingStage>(STAGES.USER_INFO);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserDetails, setCurrentUserDetails] = useState<PaymentFormUserProps | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<UserData['userRole']>(null);
  const [processError, setProcessError] = useState<string | null>(null);
  const [isCompletingProfile, setIsCompletingProfile] = useState(false); // Add loading state for profile completion
  // Initialize referralData from sessionStorage if available 
  // (should happen as soon as page loads because that's when the useEffect's dependency array of searchParams 
  // would be activated)
  const [referralData, setReferralData] = useState<{
    hashedReferralId: string;
    referrerInfo: {
      referrerId: string;
      referrerEmail: string;
      referrerFirstName: string;
      referrerUserRole: 'parent' | 'student' | 'educator';
      linkType: string;
    } | null;
  } | null>(() => {
    try {
      const stored = sessionStorage.getItem('referralData');
      if (stored) {
        console.log('✅ Loaded referral data from sessionStorage:', JSON.parse(stored));
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading referral data from sessionStorage:', error);
    }
    return null;
  });
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showPricing = shouldShowPricing();

  // SCALING CONSTRAINT: This lookup runs once per referred user signup
  // Should complete in <1 second for individual lookups
  // OPTIMIZATION THRESHOLD: Refactor at >2,500 users or >10 referral signups/day
  // Current approach: O(n) scan of all users - acceptable for <2,500 users
  // Future solution: Create 'referralMappings' collection for O(1) lookups

  useEffect(() => { //This is now kind of complicated because of the sessionStorage dependency, so double check this to simplify potentially.
    const checkReferralParams = async () => {
      // DEBUG: Log all URL parameters
      //console.log('DEBUG All URL parameters:', Object.fromEntries(searchParams.entries()));
      
      // Try both useSearchParams and direct window.location
      const refParam = searchParams.get('ref') || new URLSearchParams(window.location.search).get('ref');
      //console.log('DEBUG Referral parameter value:', refParam);
      
      // If no URL param but we have sessionStorage data, no need to fetch again
      if (!refParam) {
        const storedData = sessionStorage.getItem('referralData');
        if (storedData) {
          console.log('✅ No URL param but referral data exists in sessionStorage - skipping fetch');
          return;
        }
        console.log('❌ No referral parameter found and no sessionStorage data');
        return;
      }
      
      // If we have URL param and it matches sessionStorage, skip fetch
      const storedData = sessionStorage.getItem('referralData');
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          if (parsed.hashedReferralId === refParam) {
            console.log('✅ Referral data already loaded for this ref param - skipping fetch');
            return;
          }
        } catch (error) {
          console.error('Error parsing stored referral data:', error);
        }
      }

      console.log('✅ Detected referral parameter:', refParam);

      try {
        // Decode the base64 referral ID
        const decodedReferralId = atob(refParam);
        console.log('Decoded referral ID:', decodedReferralId);
        if (currentUserRole) {
          console.log('DEBUG currentUserRole sent from StartFreeTrialRatio line 98 to referral-lookup.ts via API call:', currentUserRole);
        } else {
          console.log('DEBUG currentUserRole is null in StartFreeTrialRatio line 100 before sending to referral-lookup.ts via API call');
        }
        
        // PERFORMANCE NOTE: API call to secure lookup - becomes expensive >2,500 users
        // At current scale (<1,000 users): ~200-500ms, ~$0.0004 per lookup
        // Monitoring: If query time >1 second, implement referralMappings collection
        const response = await fetch('/api/referral-lookup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ hashedReferralId: refParam, referringUserRole: currentUserRole })
        });

        let referrerInfo = null;
        console.log('API Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('API Response data:', data);
          if (data.success) {
            referrerInfo = data.referrerInfo;
            console.log('Found referrer:', referrerInfo);
          } else {
            console.warn('API returned success=false:', data);
          }
        } else {
          console.warn('Referral lookup API failed:', response.status);
          const errorText = await response.text();
          console.warn('API Error response:', errorText);
        }

        if (referrerInfo) {
          console.log('✅ Setting referral data in StartFreeTrialRatio:', referrerInfo);
          const newReferralData = {
            hashedReferralId: refParam,
            referrerInfo
          };
          console.log('✅ Full referralData object being set:', newReferralData);
          
          // Store in both state AND sessionStorage for persistence
          setReferralData(newReferralData);
          sessionStorage.setItem('referralData', JSON.stringify(newReferralData));
          console.log('✅ Referral data saved to sessionStorage');
        } else {
          console.warn('❌ No matching referrer found for referral ID:', refParam);
        }

      } catch (error) {
        console.error('Invalid referral ID or lookup failed:', error);
      }
    };

    checkReferralParams();
  }, [searchParams]); //10/1/2025: Potentially add currentUserRole to the dependency array

  const getActiveStep = () => {
    if (onboardingStage === STAGES.USER_INFO) return 0;
    // if (onboardingStage === STAGES.PAYMENT) return 1; // Comment out PAYMENT step
    // Adjust subsequent steps: Profile is now step 1, Completed is step 2
    if (onboardingStage === STAGES.STUDENT_PROFILE || onboardingStage === STAGES.EDUCATOR_PROFILE) return 1;
    if (onboardingStage === STAGES.COMPLETED) return 2; 
    return 0; // Default or error case
  };

  const getStepsForRole = (role: UserData['userRole']) => {
    const baseSteps = [stepDescriptions[STAGES.USER_INFO]];// Add this back later if desired to the baseSteps:
    // , stepDescriptions[STAGES.PAYMENT]];

    if (role === 'student' || role === 'parent') {
      baseSteps.push(stepDescriptions[STAGES.STUDENT_PROFILE]);
    } else if (role === 'educator') {
      baseSteps.push(stepDescriptions[STAGES.EDUCATOR_PROFILE]);
    }
    baseSteps.push('Complete'); // Final step
    return baseSteps.filter(Boolean) as string[];
  };

  const handleUserInfoComplete = (userData: UserData) => {
    // Remove lastName from validation - now optional
    if (!userData.uid || !userData.email || !userData.firstName || !userData.userRole) {
      console.error("Incomplete user data from UserInfoForm:", userData);
      setProcessError("An error occurred during sign-up. Key information is missing.");
      setOnboardingStage(STAGES.ERROR);
      return;
    }
    setCurrentUserId(userData.uid);
    setCurrentUserDetails({
      uid: userData.uid,
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
    });
    setCurrentUserRole(userData.userRole);
    // setOnboardingStage(STAGES.PAYMENT); // Pre-5/31/2025: Go to payment

    // New: Skip payment and go directly to profile creation
    if (userData.userRole === 'student' || userData.userRole === 'parent') {
      setOnboardingStage(STAGES.STUDENT_PROFILE);
    } else if (userData.userRole === 'educator') {
      setOnboardingStage(STAGES.EDUCATOR_PROFILE);
    } else {
      console.error("Invalid user role after user info:", userData.userRole);
      setProcessError("An unexpected error occurred. Invalid user role.");
      setOnboardingStage(STAGES.ERROR);
    }
  };

  /* Commented out handlePaymentComplete on 5/31/2025 as it's no longer used, but can add back in later if desired.
  const handlePaymentComplete = (data: { subscriptionId: string; customerId: string; userId: string }) => {
    if (data.userId !== currentUserId) {
      console.error("User ID mismatch after payment:", data.userId, "expected:", currentUserId);
      setProcessError("A security error occurred after payment. Please contact support.");
      setOnboardingStage(STAGES.ERROR);
      return;
    }

    if (currentUserRole === 'student' || currentUserRole === 'parent') {
      setOnboardingStage(STAGES.STUDENT_PROFILE);
    } else if (currentUserRole === 'educator') {
      setOnboardingStage(STAGES.EDUCATOR_PROFILE);
    } else { //Realistically, this should probably not ever happen, but good to handle.
      console.error("Invalid user role after payment:", currentUserRole);
      setProcessError("An unexpected error occurred. Invalid user role.");
      setOnboardingStage(STAGES.ERROR);
    }
  };
  */

  const handleProfileComplete = async () => {
    if (currentUserId && currentUserRole && currentUserDetails) {
      setIsCompletingProfile(true); // Start loading
      try {
        // Send complete user data to Klaviyo
        await sendCompleteDataToKlaviyo(currentUserId);
        
        setOnboardingStage(STAGES.COMPLETED);
        
        // Track Meta Pixel Event
        trackPixelEvent(PixelEvents.COMPLETE_CREATE_PROFILE, {
          content_name: 'User Onboarding Complete',
          content_category: 'User Sign Up',
          user_id: currentUserId,
          user_role: currentUserRole || 'unknown',
          status: 'success',
        });
        
        // Navigate to /sat with state for welcome modal
        navigate('/sat', { 
          state: { 
            showWelcome: true, 
            userName: currentUserDetails?.firstName || undefined 
          } 
        });
      } catch (error) {
        console.error("Error in profile completion:", error);
        // Continue with normal flow even if Klaviyo fails
        setOnboardingStage(STAGES.COMPLETED);
        navigate('/sat', { 
          state: { 
            showWelcome: true, 
            userName: currentUserDetails?.firstName || undefined 
          } 
        });
      } finally {
        setIsCompletingProfile(false); // End loading
      }
    }
  };

  // Helper function to collect and send complete user data to Klaviyo
  const sendCompleteDataToKlaviyo = async (userId: string) => {
    try {
      // Fetch complete user data from Firestore
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const userData = userDoc.data() as UserData;
      const now = new Date().toISOString();
      
      // Set trial end date based on user role
      let trialEndDate: string;
      if (userData.userRole === 'educator') {
        // Educators get unlimited access (set far in the future)
        const futureDate = new Date();
        futureDate.setFullYear(futureDate.getFullYear() + 10);
        trialEndDate = futureDate.toISOString();
      } else {
        // Students and parents get 14 days
        trialEndDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      }
      
      // Prepare Klaviyo data based on user role
      const klaviyoData = {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName || null,
        userId: userData.uid,
        userRole: userData.userRole,
        trial_signup_date: userData.createdAt?.toDate?.()?.toISOString() || now,
        trial_end_date: trialEndDate,
        profile_completed_date: now,
        
        // Role-specific data
        ...(userData.userRole === 'student' || userData.userRole === 'parent' ? {
          // Student/Parent SAT data
          has_taken_sat: userData.hasTakenSAT || false,
          previous_sat_math: userData.previousSATScores?.math || null,
          previous_sat_reading_writing: userData.previousSATScores?.readingWriting || null,
          target_sat_math: userData.targetScores?.math || null,
          target_sat_reading_writing: userData.targetScores?.readingWriting || null,
        } : {}),
        
        ...(userData.userRole === 'educator' ? {
          // Educator data
          organization: userData.organizationName || null,
          job_title: userData.jobTitle || null,
          state: userData.state || null,
        } : {}),
      };

      console.log('Sending data to Klaviyo:', klaviyoData);
      
      const response = await fetch('/api/klaviyo-handler', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(klaviyoData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Klaviyo API error: ${errorData.error}`);
      }

      const result = await response.json();
      console.log('Klaviyo success:', result);
      return result;
      
    } catch (error) {
      console.error('Error sending data to Klaviyo (non-blocking):', error);
      // Don't throw - this is non-blocking for user experience
      // TODO: Consider adding user notification or retry mechanism in the future
    }
  };

  const renderCurrentStep = () => {
    if (processError) {
      trackPixelEvent('SIGNUP_FLOW_ERROR', { // Custom Event
        error_message: processError,
        stage_at_error: onboardingStage, // you might need to track the stage before error
        content_category: 'User Sign Up Funnel',
      });

      return (
        <Box textAlign="center" p={3}>
          <Alert severity="error" sx={{ mb: 2 }}>{processError}</Alert>
          <Button
            variant="outlined"
            onClick={() => {
              setOnboardingStage(STAGES.USER_INFO);
              setProcessError(null);
              setCurrentUserId(null);
              setCurrentUserDetails(null);
              setCurrentUserRole(null);
            }}
          >
            Start Over
          </Button>
        </Box>
      );
    }

    switch (onboardingStage) {
      case STAGES.USER_INFO:
        return <UserInformationForm onComplete={handleUserInfoComplete} referralData={referralData} />;
      /* Comment out STAGES.PAYMENT case
      case STAGES.PAYMENT:
        if (!currentUserDetails) {
          setProcessError("Missing user information for payment. Please restart the sign-up process.");
          setOnboardingStage(STAGES.ERROR); 
          return null; 
        }
        return <StripePaymentForm user={currentUserDetails} onComplete={handlePaymentComplete} />;
      */
      case STAGES.STUDENT_PROFILE:
        if (!currentUserId) {
          setProcessError("Missing user ID for student profile. Please restart the sign-up process.");
          setOnboardingStage(STAGES.ERROR);
          return null;
        }
        return <StudentProfileForm uid={currentUserId} onComplete={handleProfileComplete} userRole={currentUserRole} isParentLoading={isCompletingProfile} />;
      case STAGES.EDUCATOR_PROFILE:
        if (!currentUserId) {
          setProcessError("Missing user ID for educator profile. Please restart the sign-up process.");
          setOnboardingStage(STAGES.ERROR);
          return null;
        }
        return <CreateEducatorProfile uid={currentUserId} onComplete={handleProfileComplete} isParentLoading={isCompletingProfile} />;
      case STAGES.COMPLETED:
        return (
          <Box textAlign="center" p={3}>
            <Typography variant="h5" gutterBottom>Registration Complete!</Typography>
            <Typography>Thank you for signing up. You will be redirected shortly.</Typography>
            <CircularProgress sx={{ mt: 2 }} />
          </Box>
        );
      default:
        return <Typography>Loading...</Typography>;
    }
  };

  const steps = getStepsForRole(currentUserRole);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', md: 'row' }, 
      minHeight: '100vh', 
      bgcolor: theme.palette.custom.beige 
    }}>
      {/* Left Column - Value Proposition (shows second on mobile, first on desktop) */}
      <Box sx={{ 
        flex: 1, 
        p: { xs: 4, md: 8 }, 
        display: 'flex', 
        flexDirection: 'column', 
        bgcolor: theme.palette.custom.beige,
        marginTop: { xs: '10vh', md: '15vh' },
        order: { xs: 2, md: 1 } // Second on mobile, first on desktop
      }}>
        {/* Main Value Proposition */}
        <Box sx={{ mb: {xs: 4, sm: 6} }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              fontSize: { xs: '2.25rem', md: '2.5rem', lg: '3rem' }, 
              fontWeight: 'bold', 
              mb: 2, 
              color: theme.palette.text.primary 
            }}
          >
            Put an Ivy League-Level SAT Tutor In Your Pocket
          </Typography>
          {showPricing ? (
            <Typography 
              component="span" 
              sx={{ 
                fontSize: { xs: '1.5rem', md: '2rem' }, 
                fontWeight: 'bold', 
                color: theme.palette.primary.main 
              }}
            >
              $120 Lifetime Access, or Just $39.99/Month.
            </Typography>
          ) : (
            <Typography
              component="span"
              sx={{
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontWeight: 'bold',
                color: theme.palette.primary.main
              }}
            >
              Free access for all students, parents, and educators.
            </Typography>
          )}
        </Box>
        
        {/* Testimonials */}
        <Box sx={{ mt: 0 }}>
          {/* Testimonial 1 */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="body1" 
              component="div" 
              sx={{ 
                fontSize: {xs: '1.25rem', md: '1.5rem'}, 
                fontStyle: 'italic', 
                color: theme.palette.text.secondary, 
                mb: 2 
              }}
            >
              <Box 
                component="span" 
                sx={{
                  fontSize: '3rem', 
                  color: theme.palette.primary.light,
                  display: 'block',
                }}
              >
              </Box>
              "It's super helpful. <Box component="span" sx={{ fontWeight: 'bold' }}>
                My SAT score went up 220 points in 6 weeks.
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box 
                component="img" 
                src={'images/high-school-student-loves-next-step-education-sat-prep-1.png'}
                alt="Student avatar" 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%',
                  bgcolor: theme.palette.primary.light
                }}
              />
              <Typography variant="body2">Sarah J., Junior</Typography>
            </Box>
          </Box>
          
          {/* Testimonial 2 
          {!isMobile && (*/}
            <Box>
              <Typography 
                variant="body1" 
              component="div" 
              sx={{ 
                fontSize: {xs: '1.25rem', md: '1.5rem'},
                fontStyle: 'italic',
                color: theme.palette.text.secondary,
                mb: 2
              }}
            >
                "I love it. <Box component="span" sx={{ fontWeight: 'bold' }}>
                  It helped me be more prepared
                </Box> because I wasn't prepared at all before."
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box 
                  component="img" 
                  src={'images/best-sat-prep-student-next-step-education.png'}
                  alt="Student avatar" 
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.light
                  }}
                />
                <Typography variant="body2">Jackie O., Sophomore</Typography>
              </Box>
            </Box>
            
          </Box>
        </Box>
      
      
      {/* Right Column - Multi-step Form (shows first on mobile, second on desktop) */}
      <Box sx={{ 
        flex: 1, 
        p: { xs: 3, md: 4 },
        bgcolor: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        order: { xs: 1, md: 2 } // First on mobile, second on desktop
      }}>
        <Box sx={{ maxWidth: 600, mx: 'auto', width: '100%', marginTop: {xs: '5vh', md: '10vh'},
          backgroundColor: '#ffffff' }}>
          <Box sx={{ mb: {xs: 3, md: 4}}}>
            {onboardingStage !== STAGES.USER_INFO && onboardingStage !== STAGES.COMPLETED && onboardingStage !== STAGES.ERROR && (
              <Stepper activeStep={getActiveStep()} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              
            )}
              <Box>
                {renderCurrentStep()}
              </Box>
            
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StartFreeTrialRatio;